import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (err) {
        setError('Post not found');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug, setPost, setError, setLoading]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    }
  };

  if (loading) {
    return (
      <main className="pt-20 min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="h-12 bg-gray-200 rounded mb-4"></div>
            <div className="h-6 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="h-96 bg-gray-200 rounded-2xl mb-8"></div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="pt-20 min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h1 className="font-serif text-4xl text-brand-dark mb-4">Post Not Found</h1>
          <p className="font-sans text-gray-600 mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="btn-gradient text-white font-sans">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="blog-post-page" className="pt-20 min-h-screen bg-white">
      {/* Hero */}
      <section className="py-12 md:py-20 bg-brand-surface">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Back Link */}
            <Link 
              to="/blog"
              className="inline-flex items-center font-sans text-sm text-gray-600 hover:text-brand-green transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>

            {/* Category */}
            <span className="inline-block px-3 py-1 bg-brand-green/10 rounded-full font-sans text-sm font-medium text-brand-green mb-6">
              {post.category}
            </span>

            {/* Title */}
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-dark mb-6">
              {post.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-6 text-gray-600">
              <span className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <span className="font-sans">{post.author}</span>
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span className="font-sans">{formatDate(post.created_at)}</span>
              </span>
              <button 
                onClick={handleShare}
                className="flex items-center gap-2 hover:text-brand-green transition-colors"
              >
                <Share2 className="w-5 h-5" />
                <span className="font-sans">Share</span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 -mt-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <img 
            src={post.image_url}
            alt={post.title}
            className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-lg"
          />
        </motion.div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {/* Excerpt */}
            <p className="font-sans text-xl text-gray-700 mb-8 leading-relaxed border-l-4 border-brand-green pl-6">
              {post.excerpt}
            </p>

            {/* Main Content */}
            <div className="prose prose-lg max-w-none">
              <ReactMarkdown
                components={{
                  h2: ({ children }) => (
                    <h2 className="font-serif text-2xl md:text-3xl text-brand-dark mt-10 mb-4">{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="font-serif text-xl md:text-2xl text-brand-dark mt-8 mb-3">{children}</h3>
                  ),
                  p: ({ children }) => (
                    <p className="font-sans text-gray-600 leading-relaxed mb-4">{children}</p>
                  ),
                  ul: ({ children }) => (
                    <ul className="space-y-2 mb-6 ml-4">{children}</ul>
                  ),
                  li: ({ children }) => (
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full bg-brand-green mt-2.5 flex-shrink-0" />
                      <span className="font-sans text-gray-600 leading-relaxed">{children}</span>
                    </li>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-brand-dark">{children}</strong>
                  ),
                  em: ({ children }) => (
                    <em className="italic text-gray-700">{children}</em>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-brand-green pl-6 my-6 italic text-gray-700">{children}</blockquote>
                  ),
                }}
              >
                {post.content}
              </ReactMarkdown>
            </div>

            {/* CTA */}
            <div className="mt-12 p-8 bg-brand-surface rounded-2xl">
              <h3 className="font-serif text-2xl text-brand-dark mb-4">
                Want to learn more?
              </h3>
              <p className="font-sans text-gray-600 mb-6">
                Get in touch with our team to discuss how we can help with your 
                sustainability goals.
              </p>
              <Link to="/contact">
                <Button className="btn-gradient text-white font-sans">
                  Schedule a Consultation
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default BlogPostPage;
